// Colour maths for brand tokens. Results match MUI's colorManipulator for
// '#rgb', '#rrggbb', 'rgb()' and 'rgba()' colours: `rgb(r, g, b)` /
// `rgba(r, g, b, a)` with channels truncated to integers.

type ParsedColor = {
  channels: [number, number, number];
  alpha: number | undefined;
};

const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const FUNCTIONAL_COLOR = /^(rgba?)\(([^)]*)\)$/;

export const parseColor = (color: string): ParsedColor => {
  const hex = HEX_COLOR.exec(color)?.[1];
  if (hex !== undefined) {
    const pairs =
      hex.length === 3
        ? Array.from(hex, digit => digit + digit)
        : (hex.match(/../g) ?? []);
    const [r = 0, g = 0, b = 0] = pairs.map(pair => parseInt(pair, 16));
    return { channels: [r, g, b], alpha: undefined };
  }
  const functional = FUNCTIONAL_COLOR.exec(color);
  if (functional) {
    const [r = 0, g = 0, b = 0, a] = (functional[2] ?? '')
      .split(',')
      .map(value => parseFloat(value));
    return {
      channels: [r, g, b],
      alpha: functional[1] === 'rgba' ? a : undefined,
    };
  }
  throw new Error(`Unsupported color: ${color}`);
};

const clamp = (value: number): number => Math.min(Math.max(value, 0), 1);

const formatColor = (
  channels: readonly number[],
  opacity: number | undefined
): string => {
  const rgb = channels.map(channel => Math.trunc(channel)).join(', ');
  return opacity === undefined
    ? `rgb(${rgb})`
    : `rgba(${rgb}, ${opacity.toString()})`;
};

/** WCAG relative luminance, rounded to 3 decimals. */
export const getLuminance = (color: string): number => {
  const [r = 0, g = 0, b = 0] = parseColor(color).channels.map(channel => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return Number((0.2126 * r + 0.7152 * g + 0.0722 * b).toFixed(3));
};

/** WCAG contrast ratio of two colours, 1–21; alpha is ignored. */
export const getContrastRatio = (
  foreground: string,
  background: string
): number => {
  const first = getLuminance(foreground);
  const second = getLuminance(background);
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
};

/** `color` moved towards white by `coefficient` (0–1). */
export const lighten = (color: string, coefficient: number): string => {
  const { channels, alpha: opacity } = parseColor(color);
  const amount = clamp(coefficient);
  return formatColor(
    channels.map(channel => channel + (255 - channel) * amount),
    opacity
  );
};

/** `color` with its alpha set to `value` (0–1). */
export const alpha = (color: string, value: number): string =>
  formatColor(parseColor(color).channels, clamp(value));
